import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart3, Shield, Users, Clock } from 'lucide-react';

const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const features = [
    {
      icon: BarChart3,
      title: 'Portfolio Visibility',
      description: 'Real-time insight across your entire project estate. No more chasing updates.'
    },
    {
      icon: Shield,
      title: 'Governance That Works',
      description: 'Frameworks that get used, not filed. Built for how teams actually operate.'
    },
    {
      icon: Users,
      title: 'Stakeholder Confidence',
      description: 'Give boards and clients the visibility they need, when they need it.'
    },
    {
      icon: Clock,
      title: 'Lifecycle Control',
      description: 'Long-term planning aligned to contract obligations and asset strategies.'
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1671250056971-49b560c1a3af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMGZhY2FkZXxlbnwwfHx8fDE3NzUzNDA5MzV8MA&ixlib=rb-4.1.0&q=85')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div 
            className="max-w-3xl"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6"
            >
              Consultancy. Intelligent Software. Control.
            </motion.p>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium tracking-tight text-slate-900 leading-[1.1]"
              data-testid="hero-headline"
            >
              Where Complex Projects
              <span className="text-slate-400"> Find Clarity</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl"
            >
              Hands-on consultancy and purpose-built software for organisations 
              managing complex project portfolios. We help you take control — 
              through better governance, clearer visibility, and smarter systems.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="mt-12 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center justify-center bg-slate-900 text-white px-8 py-4 font-medium tracking-wide hover:bg-slate-800 transition-all group"
              >
                Explore Our Services
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/videra"
                data-testid="hero-cta-videra"
                className="inline-flex items-center justify-center bg-transparent border border-slate-200 text-slate-900 px-8 py-4 font-medium tracking-wide hover:border-slate-900 transition-all"
              >
                Discover Videra Platform
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 md:py-32 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
                The Challenge
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
                Complex portfolios need more than spreadsheets
              </h2>
              <p className="mt-8 text-lg text-slate-600 leading-relaxed">
                When you're managing dozens of projects across multiple sites, 
                stakeholders, and contractors — things get complicated fast. 
                Data lives in silos. Reporting takes days. Governance becomes 
                a box-ticking exercise.
              </p>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed font-medium">
                We've been there. That's why we built a better way.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/36766683/pexels-photo-36766683.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Strategic consultation"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Two Approaches */}
      <section className="py-20 md:py-32" data-testid="approaches-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              How We Help
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Consultancy, software, or both
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Consultancy Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 border border-slate-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all"
            >
              <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-4">
                Strategic Consultancy
              </p>
              <h3 className="text-2xl font-medium text-slate-900 mb-4">
                Hands-on expertise for your toughest challenges
              </h3>
              <p className="text-slate-600 leading-relaxed mb-8">
                We work alongside your teams to implement governance frameworks, 
                optimise delivery processes, and build capability that lasts. 
                From PMO setup to full transformation programmes.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center text-slate-900 font-medium hover:text-blue-600 transition-colors group"
                data-testid="consultancy-link"
              >
                View Services
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            {/* Videra Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-slate-900 p-10"
            >
              <p className="text-xs uppercase tracking-[0.15em] text-blue-400 font-semibold mb-4">
                Videra Platform
              </p>
              <h3 className="text-2xl font-medium text-white mb-4">
                Portfolio management software that actually works
              </h3>
              <p className="text-slate-300 leading-relaxed mb-8">
                Purpose-built for complex project environments. Real-time 
                visibility, automated governance, stakeholder portals — 
                everything you need in one place.
              </p>
              <Link
                to="/videra"
                className="inline-flex items-center text-white font-medium hover:text-blue-400 transition-colors group"
                data-testid="videra-link"
              >
                Explore Videra
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32 bg-slate-50 border-y border-slate-200" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              What You Get
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Control where it matters
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 border border-slate-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-300"
                data-testid={`feature-${index}`}
              >
                <div className="w-12 h-12 bg-slate-50 flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-slate-700" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              Sectors
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Trusted in demanding environments
            </h2>
            <p className="mt-6 text-lg text-slate-600">
              Our experience spans healthcare, PFI, facilities management, and 
              infrastructure — sectors where governance isn't optional.
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-4">
            {['Healthcare & NHS', 'PFI Contracts', 'Facilities Management', 'Infrastructure', 'Capital Programmes'].map((sector, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="px-6 py-3 bg-slate-100 text-slate-700 text-sm font-medium"
              >
                {sector}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              Ready to take control?
            </h2>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
              Whether you need consultancy support, the Videra platform, or 
              both — let's talk about what's holding your portfolio back.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="inline-flex items-center mt-10 bg-white text-slate-900 px-10 py-5 font-medium tracking-wide hover:bg-slate-100 transition-all group"
            >
              Get in Touch
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
